import React, { useEffect, useState } from 'react';
import { Key, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiKeyService, migrateApiKeysFromOtherTables } from '../../lib/apiKeyService';

// Interface pour les configurations d'API récupérées

interface ApiConfigSectionProps {
  refreshTrigger?: number; // Prop pour forcer le rechargement des données
}

const ApiConfigSection: React.FC<ApiConfigSectionProps> = ({ refreshTrigger = 0 }) => {
  const [whatCmsKey, setWhatCmsKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [emeliaKey, setEmeliaKey] = useState('');
  const [dataForSeoKey, setDataForSeoKey] = useState('');
  const [dataForSeoLogin, setDataForSeoLogin] = useState('');
  const [openaiModel, setOpenaiModel] = useState('gpt-3.5-turbo');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isMigrating, setIsMigrating] = useState(false);
  
  // Liste des modèles disponibles
  const availableModels = [
    // Modèles GPT-3.5
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', description: 'Bon rapport qualité/prix', category: 'GPT-3.5' },
    { id: 'gpt-3.5-turbo-16k', name: 'GPT-3.5 Turbo 16K', description: 'Version avec contexte étendu', category: 'GPT-3.5' },
    { id: 'gpt-3.5-turbo-instruct', name: 'GPT-3.5 Turbo Instruct', description: 'Version optimisée pour les instructions', category: 'GPT-3.5' },
    
    // Modèles GPT-4
    { id: 'gpt-4', name: 'GPT-4', description: 'Plus avancé, recommandé pour les analyses complexes', category: 'GPT-4' },
    { id: 'gpt-4-32k', name: 'GPT-4 32K', description: 'GPT-4 avec contexte étendu', category: 'GPT-4' },
    { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', description: 'Version optimisée de GPT-4', category: 'GPT-4' },
    { id: 'gpt-4o', name: 'GPT-4o', description: 'Dernier modèle, plus performant', category: 'GPT-4' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Version légère de GPT-4o', category: 'GPT-4' },
    
    // Modèles spécialisés
    { id: 'gpt-4-vision-preview', name: 'GPT-4 Vision', description: 'Capacités de vision par ordinateur', category: 'Spécialisés' },
    
    // Modèles Claude (si intégration future)
    { id: 'claude-3-opus', name: 'Claude 3 Opus', description: 'Modèle le plus puissant d\'Anthropic', category: 'Claude' },
    { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet', description: 'Bon équilibre performance/coût', category: 'Claude' },
    { id: 'claude-3-haiku', name: 'Claude 3 Haiku', description: 'Version rapide et économique', category: 'Claude' },
    
    // Modèles légers
    { id: 'gpt-3.5-turbo-0125', name: 'GPT-3.5 Turbo (0125)', description: 'Version légère et rapide', category: 'Légers' },
    { id: 'llama-3-8b', name: 'Llama 3 (8B)', description: 'Modèle open-source léger', category: 'Légers' },
    { id: 'mistral-small', name: 'Mistral Small', description: 'Modèle compact de Mistral AI', category: 'Légers' }
  ];
  
  // Regrouper les modèles par catégorie
  const modelCategories = Array.from(new Set(availableModels.map(model => model.category)));

  // Migrer les clés API des anciennes tables vers api_keys si nécessaire
  useEffect(() => {
    const migrateIfNeeded = async () => {
      try {
        setIsMigrating(true);
        await migrateApiKeysFromOtherTables();
        setIsMigrating(false);
      } catch (error) {
        console.error('Erreur lors de la migration des clés API:', error);
        setIsMigrating(false);
      }
    };
    
    migrateIfNeeded();
  }, []);

  // Charger les configurations existantes
  useEffect(() => {
    const fetchApiConfigs = async () => {
      try {

        // Récupérer la clé WhatCMS
        const whatCmsConfig = await apiKeyService.getApiConfig('whatcms');
        
        if (whatCmsConfig?.api_key) {
          setWhatCmsKey(whatCmsConfig.api_key);
        }
        
        // Récupérer la clé OpenAI et ses métadonnées
        const openaiConfig = await apiKeyService.getApiConfig('openai');
        
        if (openaiConfig?.api_key) {
          setOpenaiKey(openaiConfig.api_key);
        }
        
        // Récupérer la clé Emelia.io
        const emeliaConfig = await apiKeyService.getApiConfig('emelia');
        
        if (emeliaConfig?.api_key) {
          setEmeliaKey(emeliaConfig.api_key);
        }
        
        // Récupérer la clé DataForSEO
        const dataForSeoConfig = await apiKeyService.getApiConfig('dataforseo');
        
        if (dataForSeoConfig?.api_key) {
          setDataForSeoKey(dataForSeoConfig.api_key);
          
          if (dataForSeoConfig.metadata?.login) {
            setDataForSeoLogin(dataForSeoConfig.metadata.login);
          }
        }
        
        if (openaiConfig?.metadata?.model) {
          setOpenaiModel(openaiConfig.metadata.model);
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des configurations API:', error);
        setErrorMessage('Impossible de charger les configurations API');
      }
    };

    fetchApiConfigs();
  }, [refreshTrigger, isMigrating]);

  // Sauvegarder une clé API
  const saveApiKey = async (service: string, key: string, metadata?: Record<string, any>) => {
    setSaveStatus('saving');
    setErrorMessage(null);

    try {
      
      // Utiliser le service unifié pour sauvegarder la clé API
      const success = await apiKeyService.saveApiKey(service, key, metadata);
      
      if (!success) {
        throw new Error(`Échec de la sauvegarde de la clé API pour ${service}`);
      }
      
      
      toast.success(`Clé API ${service} sauvegardée avec succès`);
      setSaveStatus('success');
      
      // Réinitialiser le statut après 3 secondes
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la clé API:', error);
      setSaveStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Erreur inconnue');
    }
  };

  // Gestionnaire pour sauvegarder la clé WhatCMS
  const saveWhatCmsKey = async () => {
    await saveApiKey('whatcms', whatCmsKey);
  };

  // Gestionnaire pour sauvegarder la clé OpenAI
  const saveOpenaiKey = async () => {
    await saveApiKey('openai', openaiKey, { model: openaiModel });
  };
  
  // Gestionnaire pour sauvegarder la clé Emelia.io
  const saveEmeliaKey = async () => {
    await saveApiKey('emelia', emeliaKey);
  };
  
  // Gestionnaire pour sauvegarder la clé DataForSEO
  const saveDataForSeoKey = async () => {
    await saveApiKey('dataforseo', dataForSeoKey, { login: dataForSeoLogin });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-semibold mb-6 flex items-center">
        <Key className="mr-2 h-5 w-5 text-blue-600" />
        Configuration des API
      </h2>
      
      <div className="space-y-8">
        {/* WhatCMS API Key */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">WhatCMS</h3>
          <p className="text-sm text-gray-600 mb-4">
            Clé API nécessaire pour la détection des CMS sur les sites web.
          </p>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <input
                type="password"
                value={whatCmsKey}
                onChange={(e) => setWhatCmsKey(e.target.value)}
                placeholder="Entrez votre clé API WhatCMS"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={saveWhatCmsKey}
              disabled={saveStatus === 'saving'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center"
            >
              {saveStatus === 'saving' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>

        {/* OpenAI API Key */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">OpenAI</h3>
          <p className="text-sm text-gray-600 mb-4">
            Clé API nécessaire pour les fonctionnalités d'IA (recommandations, analyses).
            <a 
              href="https://platform.openai.com/api-keys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 ml-1 underline"
            >
              Obtenir une clé API
            </a>
          </p>
          
          {/* Champ de clé API */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1">
              <label htmlFor="openai-key" className="block text-sm font-medium text-gray-700 mb-1">Clé API</label>
              <input
                id="openai-key"
                type="password"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder="sk-... (votre clé API OpenAI)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Sélection de modèle */}
          <div className="mb-4">
            <label htmlFor="openai-model" className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
            <select
              id="openai-model"
              value={openaiModel}
              onChange={(e) => setOpenaiModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {modelCategories.map(category => (
                <optgroup key={category} label={category}>
                  {availableModels
                    .filter(model => model.category === category)
                    .map(model => (
                      <option key={model.id} value={model.id}>
                        {model.name} - {model.description}
                      </option>
                    ))
                  }
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Le modèle sélectionné sera utilisé pour toutes les requêtes OpenAI.
              <span className="block mt-1 text-gray-600 font-medium">Note :</span>
              <span className="block">- Les modèles GPT-3.5 sont les moins coûteux</span>
              <span className="block">- Les modèles "mini" offrent un bon équilibre coût/performance</span>
              <span className="block">- L'intégration de Claude et autres modèles nécessite des API distinctes</span>
            </p>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={saveOpenaiKey}
              disabled={saveStatus === 'saving'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center"
            >
              {saveStatus === 'saving' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Emelia.io API Key */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Emelia.io</h3>
          <p className="text-sm text-gray-600 mb-4">
            Clé API nécessaire pour les campagnes d'emails et le suivi des interactions.
            <a 
              href="https://app.emelia.io/settings" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 ml-1 underline"
            >
              Obtenir une clé API
            </a>
          </p>
          
          {/* Champ de clé API */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1">
              <label htmlFor="emelia-key" className="block text-sm font-medium text-gray-700 mb-1">Clé API</label>
              <input
                id="emelia-key"
                type="password"
                value={emeliaKey}
                onChange={(e) => setEmeliaKey(e.target.value)}
                placeholder="Entrez votre clé API Emelia.io"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={saveEmeliaKey}
              disabled={saveStatus === 'saving'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center"
            >
              {saveStatus === 'saving' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* DataForSEO API Key */}
        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">DataForSEO</h3>
          <p className="text-sm text-gray-600 mb-4">
            Identifiants nécessaires pour l'analyse des mots-clés rankés et autres données SEO.
            <a 
              href="https://app.dataforseo.com/api-v3/management/clients/dashboard" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 ml-1 underline"
            >
              Accéder à DataForSEO
            </a>
          </p>
          
          {/* Champ de login */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1">
              <label htmlFor="dataforseo-login" className="block text-sm font-medium text-gray-700 mb-1">Login</label>
              <input
                id="dataforseo-login"
                type="text"
                value={dataForSeoLogin}
                onChange={(e) => setDataForSeoLogin(e.target.value)}
                placeholder="Entrez votre login DataForSEO (email)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Champ de clé API */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1">
              <label htmlFor="dataforseo-key" className="block text-sm font-medium text-gray-700 mb-1">Clé API</label>
              <input
                id="dataforseo-key"
                type="password"
                value={dataForSeoKey}
                onChange={(e) => setDataForSeoKey(e.target.value)}
                placeholder="Entrez votre clé API DataForSEO"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={saveDataForSeoKey}
              disabled={saveStatus === 'saving'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center"
            >
              {saveStatus === 'saving' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>

        {/* Messages d'état */}
        {saveStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-50 text-green-800 rounded-md text-sm flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            Configuration enregistrée avec succès
          </div>
        )}
        {saveStatus === 'error' && errorMessage && (
          <div className="mt-4 p-3 bg-red-50 text-red-800 rounded-md text-sm flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiConfigSection;
